import { useCallback, useMemo, useState } from 'react';
import { checkIfUploadingIsAllowed } from '@utils/checkIfUploadingIsAllowed';
import { checkReceiptsLimit } from '@utils/checkReceiptsLimit';
import { checkReceiptStateValue } from '@utils/checkReceiptStateValue';
import { checkReceiptIsProcessing } from '@utils/checkReceiptIsProcessing';
import {
  AppErrorState,
  AppErrorsStatusCodes,
} from '@interfaces/appErrorsStatusCodes';
import { Order } from '@interfaces/order';
import { ReceiptState, ReceiptStateValue } from '@interfaces/receipts';
import { getPaymentSearchSource } from '@utils/getPaymentSearchSource';
import { useNormalizedPathname } from '@hooks/useNormalizedPathname';
import { useTempApi } from '@hooks/useTempApi';

interface UseOrderStateProps {
  initialOrder: Order;
  initialReceiptState: ReceiptState;
  onError: (err: AppErrorState) => void;
}

export function useOrderState({
  initialOrder,
  initialReceiptState,
  onError,
}: UseOrderStateProps) {
  const normalizedPathname = useNormalizedPathname();
  const api = useTempApi(initialOrder.id);
  const source = useMemo(
    () => getPaymentSearchSource(`/${normalizedPathname}`),
    [normalizedPathname]
  );

  const [order, setOrder] = useState<Order>(initialOrder);
  const [receiptState, setReceiptState] =
    useState<ReceiptState>(initialReceiptState);
  const [isPaymentSearchActive, setIsPaymentSearchActive] = useState(
    checkReceiptIsProcessing(source, initialReceiptState, initialOrder.status)
  );
  const [isAllReceiptsRejected, setIsAllReceiptsRejected] = useState(
    checkReceiptStateValue(
      source,
      ReceiptStateValue.rejected,
      initialReceiptState
    )
  );

  const [areReceiptsUnderLimit, setAreReceiptsUnderLimit] = useState(
    checkReceiptsLimit({ receiptState: initialReceiptState, source })
  );
  const [isUploadingAllowed, setIsUploadingAllowed] = useState(
    checkIfUploadingIsAllowed(source, initialReceiptState)
  );
  const [isReceiptDisabled, setIsReceiptDisabled] = useState(
    checkReceiptStateValue(source, ReceiptStateValue.disabled, initialReceiptState)
  );

  const getOrder = useCallback(async () => {
    try {
      const [orderResponse, receiptStateResponse] = await Promise.all([
        api.order.getOrderDetail(order.id),
        api.order.getReceiptsState(order.id),
      ]);

      if (!orderResponse?.data || !receiptStateResponse?.data) {
        onError({
          errorCode: AppErrorsStatusCodes.UNEXPECTED_ERROR,
          showErrorPage: true,
        });
        return;
      }

      const newOrder = orderResponse.data;
      const newReceiptState = receiptStateResponse.data;

      setOrder(newOrder);
      setReceiptState(newReceiptState);

      setIsPaymentSearchActive(
        checkReceiptIsProcessing(source, newReceiptState, newOrder.status)
      );
      setIsAllReceiptsRejected(
        checkReceiptStateValue(
          source,
          ReceiptStateValue.rejected,
          newReceiptState
        )
      );
      setAreReceiptsUnderLimit(
        checkReceiptsLimit({ receiptState: newReceiptState, source })
      );
      setIsUploadingAllowed(checkIfUploadingIsAllowed(source, newReceiptState));
      setIsReceiptDisabled(
        checkReceiptStateValue(source, ReceiptStateValue.disabled, newReceiptState)
      );

      return { order: newOrder, receiptState: newReceiptState };
    } catch (error) {
      console.error('Error fetching order:', error);
      onError({
        errorCode: AppErrorsStatusCodes.UNEXPECTED_ERROR,
        showErrorPage: true,
      });
      return;
    }
  }, [order.id, source, onError, api]);

  return {
    order,
    receiptState,
    getOrder,
    isPaymentSearchActive,
    areReceiptsUnderLimit,
    isAllReceiptsRejected,
    isUploadingAllowed,
    isReceiptDisabled,
  };
}
